/**
 * Skrivning för importen av leadslistor (leadslistaImport.js gör planen).
 * db skickas in, så att samma kod körs i appen och i importtestet mot
 * emulatorn (tests/import). Därför bara importer med .js-ändelse.
 *
 * Varje rad skrivs i en egen batch – en rad blir antingen helt klar eller
 * inte alls. Ingen befintlig data skrivs över: på befintliga kunder fylls
 * bara tomma kategorier i, och befintliga leads rörs aldrig.
 */
import * as firestore from 'firebase/firestore';

const utan = obj => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== ''));
const idag = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Källan på leadet: "<partner>: <Källa-kolumnen>", eller bara partnern. */
export function leadKalla(partner, kalla) {
  return [partner, kalla].filter(Boolean).join(': ');
}

/** Anteckningen på ett nytt lead: kommentar och status ur listan. '' om inget finns. */
export function anteckning(rad, partner) {
  const rader = [
    rad.kommentar && `Kommentar: ${rad.kommentar}`,
    rad.status && `Status: ${rad.status}`,
  ].filter(Boolean);
  return rader.length ? [`Från leadslista (${partner}):`, ...rader].join('\n') : '';
}

/**
 * Utför planen (planeraImport). Rader med åtgärd bekrafta/hoppa_over hoppas över.
 * @param {object} o
 *   db, plan, partner, uid, flatboxProductId, departmentIds (MVL)
 *   sdk: Firestore-funktionerna – bara i testet, som måste använda samma
 *        SDK-instans (CommonJS) som emulatortestmiljön
 * @returns {{ nyaKunder, nyaLeads, ifyllda, oforandrade, overhoppade, fel, rader: [{radnr, organisation, atgard, companyId, leadId}] }}
 */
export async function utforImport({ db, plan, partner, uid, flatboxProductId, departmentIds = [], sdk = firestore }) {
  const { collection, doc, writeBatch, Timestamp } = sdk;
  const res = { nyaKunder: 0, nyaLeads: 0, ifyllda: 0, oforandrade: 0, overhoppade: 0, fel: [], rader: [] };
  for (const p of plan) {
    const { rad } = p;
    if (p.atgard === 'bekrafta' || p.atgard === 'hoppa_over') {
      res.overhoppade++;
      res.rader.push({ radnr: rad.radnr, organisation: rad.organisation, atgard: p.atgard });
      continue;
    }
    try {
      const batch = writeBatch(db);
      const nu = Timestamp.now();
      let companyId = p.kund?.id || null;

      if (p.atgard === 'ny_kund') {
        const ref = doc(collection(db, 'crmCompanies'));
        companyId = ref.id;
        batch.set(ref, utan({
          name: rad.organisation,
          status: 'prospect',
          segment: p.kategorier.segment,
          organisationstyp: p.kategorier.organisationstyp,
          kalla: partner,
          createdAt: nu, createdBy: uid, updatedAt: nu,
        }));
      } else if (Object.keys(p.fyllI).length) {
        batch.update(doc(db, 'crmCompanies', companyId), { ...p.fyllI, updatedAt: nu });
        res.ifyllda++;
      }

      let leadId = null;
      if (p.atgard === 'ny_kund' || p.atgard === 'nytt_lead') {
        let contactId = null;
        if (rad.kontaktperson) {
          const cref = doc(collection(db, 'crmCompanies', companyId, 'contacts'));
          contactId = cref.id;
          batch.set(cref, utan({ name: rad.kontaktperson, role: rad.roll, primary: false, dealRelation: partner, createdAt: nu }));
        }
        const text = anteckning(rad, partner);
        const lref = doc(collection(db, 'crmLeads'));
        leadId = lref.id;
        batch.set(lref, utan({
          title: rad.organisation,
          companyId,
          type: 'produkt',
          productId: flatboxProductId,
          status: 'identified',
          skedeSedan: nu,
          skedeshistorik: [{ fran: null, till: 'identified', tid: nu, av: uid }],
          departmentIds,
          ownerId: uid,
          kalla: leadKalla(partner, rad.kalla),
          contactIds: contactId ? [contactId] : [],
          mainContactId: contactId || undefined,
          tags: [],
          activityCount: text ? 1 : 0,
          lastActivitySummary: text || undefined,
          lastActivityDate: text ? idag() : undefined,
          lastActivityType: text ? 'note' : undefined,
          createdBy: uid, createdAt: nu, updatedAt: nu,
        }));
        if (text) {
          batch.set(doc(collection(db, 'crmLeads', leadId, 'activities')), {
            type: 'note', date: idag(), direction: 'out', summary: text,
            kalla: partner, createdBy: uid, createdAt: nu,
          });
        }
      }

      await batch.commit();
      if (p.atgard === 'ny_kund') res.nyaKunder++;
      if (leadId) res.nyaLeads++;
      if (p.atgard === 'oforandrad') res.oforandrade++;
      res.rader.push({ radnr: rad.radnr, organisation: rad.organisation, atgard: p.atgard, companyId, leadId });
    } catch (e) {
      res.fel.push({ radnr: rad.radnr, organisation: rad.organisation, fel: e.message || String(e) });
    }
  }
  return res;
}
